<template>

<Drawer :title="panelTitle" initialWidth="xl" :showDefaultCloseButton="false" @requestUnmount="close">

	<template #default="{ hide }">

		<form class="grid grid-cols-1 gap-6 pt-8">

			<div class="pb-20 px-6">

				{{ $t('Select the pupils who can participate in this activity') }}

				<div class="mt-6 flex items-center gap-2">
					<SwitchRoot
						v-model="groupByColor"
						id="groupByColor"
						class="w-[32px] h-[20px] shadow-sm flex data-[state=unchecked]:bg-gray-300 data-[state=checked]:bg-gray-800 border border-gray-300 data-[state=checked]:border-stone-700 rounded-full relative transition-[background]"
					>
						<SwitchThumb class="w-3.5 h-3.5 my-auto bg-white text-xs flex items-center justify-center shadow-xl rounded-full transition-transform translate-x-0.5 will-change-transform data-[state=checked]:translate-x-full" />
					</SwitchRoot>
					<label for="groupByColor" class="hover:cursor-pointer">{{ $t('Group by colour') }}</label>
				</div>

				<div class="mt-8">
					<div class="border-b mb-2 pb-2">
						<div class="flex items-center gap-3 w-fit">
							<input type="checkbox"
								id="select-all"
								class="checkbox checkbox-sm disabled:border disabled:border-base-content-light border-base-300 [--chkbg:white] checked:border-base-content [--chkfg:theme(colors.base-content)] disabled:bg-base-200"
								:checked="allPupilsSelected"
								:indeterminate.prop="somePupilsSelected"
								:disabled="!pupilsStore.items.length"
								@change="toggleSelectAll"
							/>
							<label for="select-all" class="grow cursor-pointer">
								{{ $t('Select all') }}
							</label>
						</div>
					</div>

					<p v-if="!pupilsStore.items.length" class="text-base-content-light py-4 italic text-center">
						{{ $t('No pupils yet') }}
					</p>

					<!-- Grouped by colour: one block per colour, with its own select-all -->
					<div v-if="groupByColor" class="ml-6">
						<div v-for="group in colorGroups" :key="group.key" class="mb-4">
							<div class="flex items-center gap-3 w-fit mb-2">
								<input type="checkbox"
									:id="'group-' + group.key"
									class="checkbox checkbox-sm disabled:border disabled:border-base-content-light border-base-300 [--chkbg:white] checked:border-base-content [--chkfg:theme(colors.base-content)] disabled:bg-base-200"
									:checked="group.selected === group.total"
									:indeterminate.prop="group.selected > 0 && group.selected < group.total"
									@change="toggleGroup(group)"
								/>
								<label :for="'group-' + group.key" class="cursor-pointer flex items-center gap-2 font-medium">
									{{ $t('All') }}
									<span class="w-4 h-4 mask mask-squircle shrink-0" :class="dotClasses(group.color)"></span>
								</label>
							</div>

							<ul class="ml-6 mb-6">
								<li v-for="pupil in group.pupils" :key="pupil.id" class="mb-2 flex gap-3 items-center">
									<input
										type="checkbox"
										:id="'pupil-' + pupil.id"
										class="checkbox checkbox-sm border-base-300 [--chkbg:white] checked:border-base-content [--chkfg:theme(colors.base-content)]"
										:checked="selectedItem.isPupilSelected(pupil)"
										@change.prevent="event => this.togglePupil(pupil, event.target.checked)"
									/>
									<label :for="'pupil-' + pupil.id" class="flex items-center gap-2 cursor-pointer">
										<span class="w-4 h-4 mask mask-squircle shrink-0" :class="dotClasses(pupil.color)"></span>
										{{ pupil.getFullName() }}
									</label>
								</li>
							</ul>
						</div>
					</div>

					<ul v-else class="ml-6">
						<li v-for="pupil in pupilsStore.items" :key="pupil.id" class="mb-2 flex gap-3 items-center">
							<input
								type="checkbox"
								:id="'pupil-' + pupil.id"
								class="checkbox checkbox-sm border-base-300 [--chkbg:white] checked:border-base-content [--chkfg:theme(colors.base-content)]"
								:checked="selectedItem.isPupilSelected(pupil)"
								@change.prevent="event => this.togglePupil(pupil, event.target.checked)"
							/>
							<label :for="'pupil-' + pupil.id" class="flex items-center gap-2 cursor-pointer">
								<span class="w-4 h-4 mask mask-squircle shrink-0" :class="dotClasses(pupil.color)"></span>
								{{ pupil.getFullName() }}
							</label>
						</li>
					</ul>
				</div>

			</div>

			<div class="flex justify-end gap-1 absolute bottom-0 w-full bg-white/50 backdrop-blur-sm z-10 p-4 mt-6 border-t border-base-200">
				<button type="button" class="btn btn-ghost" @click="hide">
					{{ $t('Cancel') }}
				</button>
				<button type="button" class="btn btn-primary" @click="applyChanges(hide)">
					{{ $t('Apply') }}
				</button>
			</div>
		</form>

	</template>
</Drawer>
</template>

<style scoped>
/* paints background white when transitioning from checked/unchecked to indeterminate, otherwise it's black' */
.checkbox:indeterminate {
	background-color: white;
}
</style>

<script lang="ts">
import {mapStores} from "pinia";
import {usePupilsStore} from "@/stores/Pupils.store";
import {LessonItem} from "@/models/LessonItem.model";
import {Pupil} from "@/models/Pupil.model";
import { OrderDirection, OrderParameter } from "@/models/OrderParameter.model";
import {useClassroomsStore} from "@/stores/Classrooms.store";

import Drawer from '@/components/ui/Drawer.v2.vue';
import { SwitchRoot, SwitchThumb } from 'reka-ui';

// group key used for pupils that have no symbol colour in the current classroom
const NO_COLOR = '__none__';

export default {
	emits: [
		'requestClose',
		'update:modelValue',
	],

	props: {
		lessonItem: {
			type: LessonItem,
			required: true,
		},
	},

	components: {
		Drawer,
		SwitchRoot, SwitchThumb,
	},

	data() {
		return {
			// false = flat list, true = pupils split into one block per colour
			groupByColor: false,
			selectedItem: null as any		// LessonItem clone, edited until the user applies
		}
	},

	computed: {
		...mapStores(usePupilsStore, useClassroomsStore),

		panelTitle() {
			let title = this.$t('Settings');
			if( this.lessonItem.activity) {
				title += ' - ' + this.lessonItem.activity.name;
			}
			return title;
		},

		/**
		 * One entry per colour actually in use in this classroom (ordered as in Pupil.COLORS),
		 * with the number of selected pupils and the total number of pupils carrying that colour.
		 * Pupils without a colour are grouped last under the NO_COLOR key.
		 */
		colorGroups() {
			const groups = new Map();

			this.pupilsStore.items.forEach((pupil) => {
				const color = pupil.color ?? null;
				const key = color ?? NO_COLOR;

				if (!groups.has(key)) {
					groups.set(key, { key, color, total: 0, selected: 0, pupils: [] });
				}

				const group = groups.get(key);
				group.total++;
				group.pupils.push(pupil);
				if (this.selectedItem?.isPupilSelected(pupil)) {
					group.selected++;
				}
			});

			const ordered = Pupil.COLORS
				.filter((color: string) => groups.has(color))
				.map((color: string) => groups.get(color));

			// any colour not covered by Pupil.COLORS, followed by the colourless pupils
			groups.forEach((group) => {
				if (group.key !== NO_COLOR && !ordered.includes(group)) {
					ordered.push(group);
				}
			});
			if (groups.has(NO_COLOR)) {
				ordered.push(groups.get(NO_COLOR));
			}

			return ordered;
		},

		selectedCount(): number {
			return this.pupilsStore.items.filter((pupil) => this.selectedItem?.isPupilSelected(pupil)).length;
		},

		allPupilsSelected(): boolean {
			return this.pupilsStore.items.length > 0 && this.selectedCount === this.pupilsStore.items.length;
		},

		somePupilsSelected(): boolean {
			return this.selectedCount > 0 && this.selectedCount < this.pupilsStore.items.length;
		},
	},

	async mounted() {
		await this.pupilsStore.loadAllForClassroom();

		// list the pupils by first name, whatever order they were loaded in
		const byFirstName = new OrderParameter('firstName', OrderDirection.ASC, 'lastName');
		(this.pupilsStore.items as Pupil[]).sort((a, b) => this.pupilsStore.orderCompare(a, b, byFirstName));
	},

	created	() {
		this.selectedItem = this.lessonItem.clone();
	},

	methods: {
		close() {
			this.$emit('requestClose');
		},

		applyChanges(closeDrawerCallback) {
			// ... update pupils attribute value
			this.$emit('update:modelValue', this.selectedItem);
			closeDrawerCallback();
		},

		/** Selects (or clears) every pupil. */
		toggleSelectAll() {
			const select = !this.allPupilsSelected;
			this.pupilsStore.items.forEach((pupil) => this.selectedItem.setPupilSelected(pupil, select));
		},

		togglePupil(pupil: Pupil, selected: boolean) {
			this.selectedItem.setPupilSelected(pupil, selected);
		},

		/** Selects (or clears) every pupil of one colour group. */
		toggleGroup(group: { selected: number; total: number; pupils: Pupil[] }) {
			const select = group.selected < group.total;
			group.pupils.forEach((pupil) => this.selectedItem.setPupilSelected(pupil, select));
		},

		dotClasses(color: string | null): string {
			if (!color) {
				return 'bg-base border-base-200';
			}
			return `bg-${color}-300`;
		},
	}
}
</script>
